import {HttpClient} from '@angular/common/http';
import {Component, DestroyRef, Inject, Input, OnInit, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {AuthService} from '../../../service/auth.service';
import {Observable, map} from 'rxjs';

interface ClaimedFileDto {
  pureOriginalId: number;
  winOlsDbName: string;
  ecuBuild: string;
  vehicleProducer: string;
  vehicleSeries: string;
  vehicleModel: string;
  claimedAt: string;
}

interface ClaimedUserDto {
  userId: number | null;
  firstName: string;
  lastName: string;
  claimedFilesCount: number;
  claimedFiles: ClaimedFileDto[];
}

interface MyClaimedFileDto {
  id: number;
  winOlsDbName: string;
  ecuBuild: string;
  vehicleProducer: string;
  vehicleSeries: string;
  vehicleModel: string;
  claimedAt: string;
}

interface OwnerSummary {
  name: string;
  count: number;
  files: FileRow[];
}

interface ClaimedOverview {
  files: FileRow[];
  owners: OwnerSummary[];
}

interface FileRow {
  pureOriginalId: number;
  fileName: string;
  metadata: string;
  owner: string;
  claimedAt: string;
  ago: string;
}

@Component({
  selector: 'app-claimed-files-v3-card',
  templateUrl: './claimed-files-v3-card.component.html',
  styleUrls: ['./claimed-files-v3-card.component.css']
})
export class ClaimedFilesV3CardComponent implements OnInit {
  @Input() mode: 'all' | 'mine' = 'all';

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly files = signal<FileRow[]>([]);
  readonly owners = signal<OwnerSummary[]>([]);
  readonly isLoading = signal(true);
  readonly loadError = signal(false);
  readonly expanded = signal(false);
  readonly selectedOwnerIndex = signal<number | null>(null);
  readonly selectedOwner = computed(() => {
    const index = this.selectedOwnerIndex();
    return index === null ? null : this.owners()[index] || null;
  });
  readonly activeCount = computed(() => this.files().length);
  readonly canOpen = computed(() => !this.isLoading() && !this.loadError() && this.activeCount() > 0);
  readonly visibleFiles = computed(() => this.expanded() ? this.files() : this.files().slice(0, 3));

  constructor(@Inject('API_URL') private readonly apiUrl: string) {}

  ngOnInit(): void {
    this.loadClaimed();
  }

  openSection(): void {
    if (!this.canOpen()) return;
    this.expanded.update(value => !value);
  }

  selectOwner(index: number): void {
    if (this.mode !== 'all' || !this.canOpen() || !this.owners()[index]) return;
    this.selectedOwnerIndex.update(current => current === index ? null : index);
  }

  canOpenFile(): boolean {
    return this.authService.isAdmin() || this.authService.isModerator();
  }

  openFile(pureOriginalId: number): void {
    if (!this.canOpen() || !this.canOpenFile()) return;
    this.router.navigate(['/app/pure-originals/pure-original-details'], {
      queryParams: {pureOriginalId}
    });
  }

  trackFile(_index: number, file: FileRow): number {
    return file.pureOriginalId;
  }

  loadClaimed(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.expanded.set(false);
    this.selectedOwnerIndex.set(null);
    const request: Observable<ClaimedOverview> = this.mode === 'mine'
      ? this.http.get<MyClaimedFileDto[]>(`${this.apiUrl}/pureOriginals/getMyClaimedFiles`).pipe(
          map(files => ({
            files: (files || []).map(file => this.toFileRow({...file, pureOriginalId: file.id}, '')),
            owners: []
          }))
        )
      : this.http.get<ClaimedUserDto[]>(`${this.apiUrl}/dashboard/pure-original/claimed/users`).pipe(
          map(users => {
            const overview: ClaimedOverview = {files: [], owners: []};
            for (const user of users || []) {
              const owner = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
                || (user.userId != null ? `User #${user.userId}` : 'Unknown user');
              const claimedFiles = user.claimedFiles || [];
              if (claimedFiles.length > 0) {
                const files = claimedFiles.map(file => this.toFileRow(file, owner))
                  .sort((a, b) => this.timestamp(b.claimedAt) - this.timestamp(a.claimedAt));
                overview.owners.push({name: owner, count: user.claimedFilesCount ?? files.length, files});
                overview.files.push(...files);
              }
            }
            return overview;
          })
        );

    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: overview => {
        this.files.set(overview.files.sort((a, b) => this.timestamp(b.claimedAt) - this.timestamp(a.claimedAt)));
        this.owners.set(overview.owners);
        this.isLoading.set(false);
      },
      error: () => {
        this.files.set([]);
        this.owners.set([]);
        this.loadError.set(true);
        this.isLoading.set(false);
      }
    });
  }

  private toFileRow(file: ClaimedFileDto, owner: string): FileRow {
    return {
      pureOriginalId: file.pureOriginalId,
      fileName: file.winOlsDbName || file.ecuBuild || `PO #${file.pureOriginalId}`,
      metadata: [file.ecuBuild, file.vehicleProducer, file.vehicleSeries, file.vehicleModel]
        .filter(Boolean).join(' · '),
      owner,
      claimedAt: file.claimedAt,
      ago: this.relativeTime(file.claimedAt)
    };
  }

  private timestamp(date: string): number {
    if (!date) return 0;
    // The API serializes LocalDateTime as dd-MM-yyyy-HH-mm-ss.
    const parts = /^(\d{2})-(\d{2})-(\d{4})-(\d{2})-(\d{2})-(\d{2})$/.exec(date);
    if (parts) {
      return new Date(+parts[3], +parts[2] - 1, +parts[1], +parts[4], +parts[5], +parts[6]).getTime();
    }
    return Date.parse(date) || 0;
  }

  private relativeTime(date: string): string {
    const timestamp = this.timestamp(date);
    if (!timestamp) return '';
    const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60000));
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
    return `${Math.floor(minutes / 1440)}d`;
  }
}
